import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { KpiWidgetComponent } from '../../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { BaseWidgetComponent } from '../../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../../_shared/services/filter.service';
import { MaintenanceOverviewService } from '../../../services/maintenance-overview.service';
import { ModalService } from '../../../../../../_shared/services/modals/modal.service';
import { OverdueMaintenanceModalComponent } from '../../modals/overdue-maintenance-modal/overdue-maintenance-modal.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { KpiStatsRequest } from '../../../models/maintenance-overview-request.model';
import { MaintenanceModel } from '../../../../../_shared/enums/maintenance-model.enum';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { TranslocoService } from '@jsverse/transloco';
import { formatCost, formatCostDifference, formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';

@Component({
  selector: 'app-overdue-widget',
  standalone: true,
  imports: [KpiWidgetComponent],
  templateUrl: './overdue-widget.component.html',
  styleUrl: './overdue-widget.component.scss'
})
export class OverdueWidgetComponent extends BaseWidgetComponent implements OnChanges {
  @Input() maintenanceModel: MaintenanceModel | null = null;
  @Input() selectedFilter: string;

  hasError: boolean = false;
  errorMessage: string = '';
  currencySymbol: string = '';
  currencyCode: string = '';
  
  private maintenanceService = inject(MaintenanceOverviewService);
  private errorService = inject(ErrorHandlerService);
  private modalService = inject(ModalService);
  private authService = inject(AuthService);
  private transloco = inject(TranslocoService);

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.title = this.transloco.translate('maintenanceOverview.widgets.overdue');
    this.isLoading = true;
    this.value = 0;
    this.percentageDifference = 0;
    this.totalCost = 0;
    this.difference = 0;

    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maintenanceModel'] && !changes['maintenanceModel'].firstChange) {
      if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
        this.onFilterChangeAsync();
      }
    }
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const request: KpiStatsRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        maintenanceModel: this.maintenanceModel
      }
      const response = await this.maintenanceService.getOverdueKpiStats(request, this.cancelSubject);
      this.value = response.data?.totalValue ?? 0;
      this.percentageDifference = response.data?.percentageChange ?? 0;
      this.totalCost = response.data?.cost ?? 0;
      this.difference = response.data?.difference ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: (errorMessage) => {
          this.hasError = true;
          this.errorMessage = errorMessage;
          this.isLoading = false;
        }
      });
    }
  }

  public override formatValue(value: number): string {
    return formatValue(value);
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  openBreakdownModal(): void {
    this.modalService.open(OverdueMaintenanceModalComponent, {
      size: 'lg',
      centered: true
    });
  }
}